import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiAlertCircle,
  FiCalendar,
  FiCheckCircle,
  FiDownload,
  FiEdit2,
  FiEye,
  FiFileText,
  FiFilter,
  FiMapPin,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiTrash2,
  FiTrendingUp,
  FiUsers,
  FiX
} from 'react-icons/fi';
import zovcurLogo from '../../assets/logo/logo.jpg';
import { generateZovcurInvoicePdf } from '../../utils/generateZovcurInvoicePdf';
import {
  deleteInvoice as deleteInvoiceApi,
  fetchAllInvoices,
  purgeLocalInvoiceStorage,
  updateInvoicePayment
} from '../../utils/zovcurInvoiceApi';
import {
  applyInvoiceCompanyDefaults,
  computeInvoiceTotals,
  formatProductName,
  getInvoiceStateLabel,
  INDIAN_STATES
} from '../../utils/zovcurInvoiceStore';

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatMoney = (value) =>
  `₹${(Number(value) || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;

const formatCompactMoney = (value) => {
  const n = Number(value) || 0;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
  return formatMoney(n);
};

const StatCard = ({ label, value, sub, icon: Icon, tone = 'navy' }) => {
  const tones = {
    navy: { value: 'text-[#1a365d]', icon: 'bg-[#e8f0f4] text-[#1a365d]' },
    teal: { value: 'text-teal-700', icon: 'bg-teal-50 text-teal-700' },
    amber: { value: 'text-amber-700', icon: 'bg-amber-50 text-amber-700' },
    emerald: { value: 'text-emerald-700', icon: 'bg-emerald-50 text-emerald-700' },
    rose: { value: 'text-rose-700', icon: 'bg-rose-50 text-rose-700' }
  };
  const t = tones[tone] || tones.navy;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
          <p className={`mt-1 text-2xl font-bold truncate ${t.value}`}>{value}</p>
          {sub ? <p className="text-xs text-slate-400 mt-1">{sub}</p> : null}
        </div>
        <div className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${t.icon}`}>
          <Icon className="text-lg" />
        </div>
      </div>
    </div>
  );
};

const Orders = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewInvoice, setViewInvoice] = useState(null);
  const [pdfLoadingId, setPdfLoadingId] = useState(null);
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [stateFilter, setStateFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      purgeLocalInvoiceStorage();
      const data = await fetchAllInvoices();
      setInvoices(data);
    } catch {
      alert('Failed to load invoices from database.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const filteredInvoices = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return invoices.filter((inv) => {
      if (stateFilter !== 'all' && getInvoiceStateLabel(inv) !== stateFilter) return false;
      const status = inv.paymentStatus || 'Unpaid';
      if (paymentFilter !== 'all' && status !== paymentFilter) return false;
      if (q) {
        const haystack = [
          inv.invoiceNumber,
          inv.invoiceDetails?.billNo,
          inv.customer?.doctor,
          inv.invoiceDetails?.salesmanName,
          getInvoiceStateLabel(inv),
          inv.invoiceDetails?.billType
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [invoices, stateFilter, paymentFilter, searchTerm]);

  const stateCounts = useMemo(() => {
    const counts = {};
    invoices.forEach((inv) => {
      const label = getInvoiceStateLabel(inv);
      if (label !== '—') {
        counts[label] = (counts[label] || 0) + 1;
      }
    });
    return counts;
  }, [invoices]);

  const stats = useMemo(() => {
    const now = new Date();
    const month = now.getMonth();
    const year = now.getFullYear();
    let totalAmount = 0;
    let paidAmount = 0;
    let unpaidAmount = 0;
    let paidCount = 0;
    let unpaidCount = 0;
    let totalQty = 0;
    let totalGst = 0;
    let thisMonth = 0;
    let thisMonthAmount = 0;
    const states = new Set();
    const customers = new Set();

    filteredInvoices.forEach((inv) => {
      const totals = computeInvoiceTotals(inv.products);
      const amount = totals.roundedNetAmount;
      totalAmount += amount;
      totalQty += totals.quantity;
      totalGst += totals.gstTotal;

      const isPaid = (inv.paymentStatus || 'Unpaid') === 'Paid';
      if (isPaid) {
        paidAmount += amount;
        paidCount += 1;
      } else {
        unpaidAmount += amount;
        unpaidCount += 1;
      }

      const billDate = inv.invoiceDetails?.billDate
        ? new Date(inv.invoiceDetails.billDate)
        : null;
      if (
        billDate &&
        !Number.isNaN(billDate.getTime()) &&
        billDate.getMonth() === month &&
        billDate.getFullYear() === year
      ) {
        thisMonth += 1;
        thisMonthAmount += amount;
      }

      const state = getInvoiceStateLabel(inv);
      if (state !== '—') states.add(state);

      const customer = inv.customer?.doctor;
      if (customer) customers.add(customer);
    });

    return {
      count: filteredInvoices.length,
      totalAmount,
      paidAmount,
      unpaidAmount,
      paidCount,
      unpaidCount,
      totalQty,
      totalGst,
      thisMonth,
      thisMonthAmount,
      statesCount: states.size,
      customersCount: customers.size
    };
  }, [filteredInvoices]);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (searchTerm.trim()) n += 1;
    if (stateFilter !== 'all') n += 1;
    if (paymentFilter !== 'all') n += 1;
    return n;
  }, [searchTerm, stateFilter, paymentFilter]);

  const clearFilters = () => {
    setSearchTerm('');
    setStateFilter('all');
    setPaymentFilter('all');
  };

  const topStates = useMemo(
    () =>
      Object.entries(stateCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5),
    [stateCounts]
  );

  const handleDelete = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoiceDetails?.billNo || inv.invoiceNumber}?`)) return;
    try {
      await deleteInvoiceApi(inv.id);
      await refresh();
      if (viewInvoice?.id === inv.id) setViewInvoice(null);
    } catch {
      alert('Failed to delete invoice.');
    }
  };

  const handleEdit = (inv) => {
    navigate(`/invoices?edit=${inv.id}`);
  };

  const handleMarkPayment = async (inv, nextStatus) => {
    if (!inv?.id) return;
    setPaymentSaving(true);
    try {
      const updated = await updateInvoicePayment(inv.id, nextStatus);
      // Only merge payment fields — never replace the whole invoice from a partial response
      setInvoices((prev) =>
        prev.map((item) =>
          item.id === inv.id
            ? {
                ...item,
                paymentStatus: updated.paymentStatus || nextStatus,
                paidAt: updated.paidAt ?? (nextStatus === 'Paid' ? new Date().toISOString() : null),
                // Prefer full server doc if it still has products; otherwise keep local data
                ...(Array.isArray(updated.products) && updated.products.length > 0
                  ? {
                      customer: updated.customer,
                      invoiceDetails: updated.invoiceDetails,
                      products: updated.products,
                      bankDetails: updated.bankDetails,
                      company: updated.company,
                      invoiceNumber: updated.invoiceNumber
                    }
                  : {})
              }
            : item
        )
      );
      setViewInvoice((prev) =>
        prev && prev.id === inv.id
          ? {
              ...prev,
              paymentStatus: updated.paymentStatus || nextStatus,
              paidAt: updated.paidAt ?? (nextStatus === 'Paid' ? new Date().toISOString() : null)
            }
          : prev
      );
    } catch {
      alert('Failed to update payment status.');
    } finally {
      setPaymentSaving(false);
    }
  };

  const handleDownloadPdf = async (inv) => {
    setPdfLoadingId(inv.id);
    try {
      await generateZovcurInvoicePdf(applyInvoiceCompanyDefaults(inv), zovcurLogo);
    } catch {
      alert('Failed to generate PDF.');
    } finally {
      setPdfLoadingId(null);
    }
  };

  const viewTotals = viewInvoice ? computeInvoiceTotals(viewInvoice.products) : null;

  return (
    <div className="min-h-screen bg-[#f4f7fa] p-4 md:p-6 font-[Montserrat,sans-serif]">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#1a365d] flex items-center gap-2">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#1a365d] text-white">
                <FiFileText />
              </span>
              Orders
            </h1>
            <p className="text-slate-500 mt-1">
              Zovcur Pharma invoices — track payments, download PDFs, and manage bills
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => refresh()}
              className="px-4 py-2 rounded-xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 flex items-center gap-2 shadow-sm"
            >
              <FiRefreshCw className={loading ? 'animate-spin' : ''} /> Refresh
            </button>
            <button
              type="button"
              onClick={() => navigate('/invoices')}
              className="px-4 py-2 rounded-xl bg-[#1a365d] text-white hover:bg-[#153050] flex items-center gap-2 shadow-md"
            >
              <FiPlus /> New Invoice
            </button>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-4">
          <StatCard
            label="Invoices"
            value={stats.count}
            sub={activeFilterCount ? 'Filtered view' : `${invoices.length} overall`}
            icon={FiFileText}
          />
          <StatCard
            label="Total Amount"
            value={formatCompactMoney(stats.totalAmount)}
            sub={formatMoney(stats.totalAmount)}
            icon={FiTrendingUp}
            tone="teal"
          />
          <StatCard
            label="Received"
            value={formatCompactMoney(stats.paidAmount)}
            sub={`${stats.paidCount} paid`}
            icon={FiCheckCircle}
            tone="emerald"
          />
          <StatCard
            label="Pending"
            value={formatCompactMoney(stats.unpaidAmount)}
            sub={`${stats.unpaidCount} unpaid`}
            icon={FiAlertCircle}
            tone="amber"
          />
          <StatCard
            label="This Month"
            value={stats.thisMonth}
            sub={formatCompactMoney(stats.thisMonthAmount)}
            icon={FiCalendar}
            tone="teal"
          />
          <StatCard
            label="Customers"
            value={stats.customersCount}
            sub={`${stats.statesCount} state${stats.statesCount !== 1 ? 's' : ''} · ${stats.totalQty} strips`}
            icon={FiUsers}
          />
        </div>

        {/* Top states */}
        {topStates.length > 0 && (
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm mb-4">
            <div className="flex items-center gap-2 mb-3">
              <FiMapPin className="text-teal-600" />
              <h2 className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                Top States
              </h2>
            </div>
            <div className="flex flex-wrap gap-2">
              {topStates.map(([state, count]) => {
                const pct = invoices.length ? Math.round((count / invoices.length) * 100) : 0;
                const active = stateFilter === state;
                return (
                  <button
                    key={state}
                    type="button"
                    onClick={() => setStateFilter(active ? 'all' : state)}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                      active
                        ? 'bg-[#1a365d] text-white border-[#1a365d]'
                        : 'bg-slate-50 text-slate-700 border-slate-200 hover:border-teal-400 hover:bg-teal-50'
                    }`}
                  >
                    <span>{state}</span>
                    <span
                      className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                        active ? 'bg-white/20' : 'bg-teal-100 text-teal-800'
                      }`}
                    >
                      {count} · {pct}%
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Search / filters */}
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm mb-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search invoice, doctor, salesman, state…"
                className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500"
              />
            </div>
            <button
              type="button"
              onClick={() => setShowFilters((v) => !v)}
              className={`px-4 py-2.5 rounded-xl border text-sm font-semibold flex items-center gap-2 ${
                showFilters || activeFilterCount
                  ? 'border-teal-300 bg-teal-50 text-teal-800'
                  : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
              }`}
            >
              <FiFilter /> Filters
              {activeFilterCount > 0 ? (
                <span className="ml-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#1a365d] px-1.5 text-[11px] text-white">
                  {activeFilterCount}
                </span>
              ) : null}
            </button>
            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={clearFilters}
                className="px-3 py-2.5 rounded-xl text-sm font-semibold text-rose-600 hover:bg-rose-50 flex items-center gap-1"
              >
                <FiX /> Clear
              </button>
            )}
          </div>

          {showFilters ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 pt-4 border-t border-slate-100">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">State</label>
                <select
                  value={stateFilter}
                  onChange={(e) => setStateFilter(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm"
                >
                  <option value="all">All States ({invoices.length})</option>
                  {INDIAN_STATES.filter((s) => stateCounts[s.name]).map((s) => (
                    <option key={s.code} value={s.name}>
                      {s.name} ({stateCounts[s.name]})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Payment</label>
                <select
                  value={paymentFilter}
                  onChange={(e) => setPaymentFilter(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm"
                >
                  <option value="all">All</option>
                  <option value="Unpaid">Unpaid</option>
                  <option value="Paid">Paid</option>
                </select>
              </div>
            </div>
          ) : null}
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50/60">
            <p className="text-sm text-slate-600">
              Showing <strong className="text-[#1a365d]">{filteredInvoices.length}</strong> of{' '}
              <strong>{invoices.length}</strong> invoices
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px] text-sm border-collapse">
              <thead className="sticky top-0 z-10">
                <tr className="bg-[#1a365d] text-white">
                  <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap">
                    Invoice
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap">
                    Date
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap">
                    Doctor / Customer
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap">
                    Salesman
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap">
                    State
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap">
                    Type
                  </th>
                  <th className="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wide whitespace-nowrap">
                    Amount
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-semibold uppercase tracking-wide whitespace-nowrap">
                    Payment
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-semibold uppercase tracking-wide whitespace-nowrap">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={9} className="py-16 text-center text-slate-500">
                      <div className="inline-block animate-spin rounded-full h-9 w-9 border-b-2 border-[#1a365d] mb-3" />
                      <p className="text-sm">Loading invoices…</p>
                    </td>
                  </tr>
                ) : filteredInvoices.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="py-16 text-center text-slate-500">
                      {invoices.length === 0
                        ? 'No invoices yet. Create one from New Invoice.'
                        : 'No invoices match your search or filters.'}
                    </td>
                  </tr>
                ) : (
                  filteredInvoices.map((inv, index) => {
                    const totals = computeInvoiceTotals(inv.products);
                    const customerLabel = inv.customer?.doctor || '—';
                    const stateLabel = getInvoiceStateLabel(inv);
                    const isPaid = (inv.paymentStatus || 'Unpaid') === 'Paid';
                    return (
                      <tr
                        key={inv.id}
                        className={`border-b border-slate-100 hover:bg-teal-50/40 transition-colors ${
                          index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'
                        }`}
                      >
                        <td className="px-3 py-3 whitespace-nowrap">
                          <span className="font-semibold text-[#1a365d]">
                            {inv.invoiceNumber}
                          </span>
                          {inv.invoiceDetails?.billNo &&
                          inv.invoiceDetails.billNo !== inv.invoiceNumber ? (
                            <p className="text-xs text-slate-400 mt-0.5">
                              Bill {inv.invoiceDetails.billNo}
                            </p>
                          ) : null}
                        </td>
                        <td className="px-3 py-3 text-slate-600 whitespace-nowrap">
                          {formatDate(inv.invoiceDetails?.billDate)}
                        </td>
                        <td className="px-3 py-3 text-slate-800 font-medium min-w-[140px]">
                          {customerLabel}
                        </td>
                        <td className="px-3 py-3 text-slate-700 whitespace-nowrap">
                          {String(inv.invoiceDetails?.salesmanName || '').trim() || '—'}
                        </td>
                        <td className="px-3 py-3 text-slate-600 whitespace-nowrap">{stateLabel}</td>
                        <td className="px-3 py-3 text-slate-600 whitespace-nowrap">
                          {inv.invoiceDetails?.billType || '—'}
                        </td>
                        <td className="px-3 py-3 text-right font-semibold text-slate-800 whitespace-nowrap">
                          {formatMoney(totals.roundedNetAmount)}
                        </td>
                        <td className="px-3 py-3 text-center whitespace-nowrap">
                          <span
                            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ${
                              isPaid
                                ? 'bg-emerald-50 text-emerald-700'
                                : 'bg-amber-50 text-amber-800'
                            }`}
                          >
                            {isPaid ? 'Paid' : 'Unpaid'}
                          </span>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <div className="flex items-center justify-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => handleDownloadPdf(inv)}
                              disabled={pdfLoadingId === inv.id}
                              className="p-2 rounded-lg text-teal-600 hover:bg-teal-50 disabled:opacity-50"
                              title="Download PDF"
                            >
                              <FiDownload />
                            </button>
                            <button
                              type="button"
                              onClick={() => setViewInvoice(inv)}
                              className="p-2 rounded-lg text-[#1a365d] hover:bg-slate-100"
                              title="View"
                            >
                              <FiEye />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleEdit(inv)}
                              className="p-2 rounded-lg text-emerald-600 hover:bg-emerald-50"
                              title="Edit"
                            >
                              <FiEdit2 />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(inv)}
                              className="p-2 rounded-lg text-rose-600 hover:bg-rose-50"
                              title="Delete"
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* View modal */}
      {viewInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between gap-3 bg-[#1a365d] text-white rounded-t-2xl">
              <div className="flex items-center gap-3 min-w-0">
                <h2 className="text-lg font-bold truncate">
                  Invoice {viewInvoice.invoiceNumber}
                </h2>
                <span
                  className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold whitespace-nowrap ${
                    (viewInvoice.paymentStatus || 'Unpaid') === 'Paid'
                      ? 'bg-emerald-400/20 text-emerald-100'
                      : 'bg-amber-400/20 text-amber-100'
                  }`}
                >
                  {(viewInvoice.paymentStatus || 'Unpaid') === 'Paid' ? 'Paid' : 'Unpaid'}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setViewInvoice(null)}
                className="p-2 rounded-lg text-white/80 hover:bg-white/10"
                title="Close"
              >
                <FiX size={18} />
              </button>
            </div>

            <div className="p-5 space-y-4 text-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
                <p>
                  <span className="text-slate-500">Bill Date:</span>{' '}
                  <span className="font-medium text-slate-800">
                    {formatDate(viewInvoice.invoiceDetails?.billDate)}
                  </span>
                </p>
                <p>
                  <span className="text-slate-500">Order Date:</span>{' '}
                  <span className="font-medium text-slate-800">
                    {formatDate(viewInvoice.invoiceDetails?.orderDate)}
                  </span>
                </p>
                <p>
                  <span className="text-slate-500">Due Date:</span>{' '}
                  <span className="font-medium text-slate-800">
                    {formatDate(viewInvoice.invoiceDetails?.dueDate)}
                  </span>
                </p>
                <p>
                  <span className="text-slate-500">Doctor:</span>{' '}
                  <span className="font-medium text-slate-800">
                    {viewInvoice.customer?.doctor || '—'}
                  </span>
                </p>
                <p>
                  <span className="text-slate-500">State:</span>{' '}
                  <span className="font-medium text-slate-800">
                    {getInvoiceStateLabel(viewInvoice)}
                  </span>
                </p>
                <p>
                  <span className="text-slate-500">State Code:</span>{' '}
                  <span className="font-medium text-slate-800">
                    {viewInvoice.invoiceDetails?.stateCode || '—'}
                  </span>
                </p>
                <p>
                  <span className="text-slate-500">Salesman:</span>{' '}
                  <span className="font-semibold text-[#0e3d52]">
                    {String(viewInvoice.invoiceDetails?.salesmanName || '').trim() || '—'}
                  </span>
                </p>
                <p>
                  <span className="text-slate-500">Delivery:</span>{' '}
                  <span className="font-medium text-slate-800">
                    {viewInvoice.invoiceDetails?.deliveryType || '—'}
                  </span>
                </p>
                {(viewInvoice.paymentStatus || 'Unpaid') === 'Paid' && viewInvoice.paidAt ? (
                  <p className="sm:col-span-2">
                    <span className="text-slate-500">Paid At:</span>{' '}
                    <span className="font-medium text-emerald-700">
                      {formatDate(viewInvoice.paidAt)}
                    </span>
                  </p>
                ) : null}
              </div>

              <div className="border rounded-xl overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-[#1a365d] text-white">
                      <th className="px-3 py-2 text-left">Product</th>
                      <th className="px-3 py-2 text-center">Qty</th>
                      <th className="px-3 py-2 text-right">Rate</th>
                      <th className="px-3 py-2 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewInvoice.products
                      .filter((p) => p.product || Number(p.qty))
                      .map((p, i) => {
                        const qty = Number(p.qty) || 0;
                        const rate = Number(p.rate) || 0;
                        const gst = (qty * rate * (Number(p.gstPercent) || 0)) / 100;
                        const total = qty * rate + gst;
                        return (
                          <tr key={p.id || i} className="border-t border-slate-100">
                            <td className="px-3 py-2 font-bold uppercase text-slate-800">
                              {formatProductName(p.product)}
                            </td>
                            <td className="px-3 py-2 text-center">{p.qty}</td>
                            <td className="px-3 py-2 text-right">{rate.toFixed(2)}</td>
                            <td className="px-3 py-2 text-right font-semibold">
                              {total.toFixed(2)}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>

              {viewTotals && (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-1.5">
                  <div className="flex justify-between text-slate-600">
                    <span>Base Amount</span>
                    <span>{formatMoney(viewTotals.baseAmount)}</span>
                  </div>
                  <div className="flex justify-between text-slate-600">
                    <span>GST Total</span>
                    <span>{formatMoney(viewTotals.gstTotal)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-base text-[#1a365d] pt-1 border-t border-slate-200">
                    <span>Invoice Amount</span>
                    <span>{formatMoney(viewTotals.roundedNetAmount)}</span>
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-1">
                {(viewInvoice.paymentStatus || 'Unpaid') === 'Paid' ? (
                  <button
                    type="button"
                    onClick={() => handleMarkPayment(viewInvoice, 'Unpaid')}
                    disabled={paymentSaving}
                    className="px-4 py-2.5 rounded-xl border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 flex items-center gap-2 disabled:opacity-60 font-semibold"
                  >
                    Mark Unpaid
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleMarkPayment(viewInvoice, 'Paid')}
                    disabled={paymentSaving}
                    className="px-4 py-2.5 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-2 disabled:opacity-60 font-semibold"
                  >
                    <FiCheckCircle /> {paymentSaving ? 'Saving…' : 'Mark Paid'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => handleDownloadPdf(viewInvoice)}
                  disabled={pdfLoadingId === viewInvoice.id}
                  className="px-4 py-2.5 rounded-xl bg-teal-600 text-white hover:bg-teal-700 flex items-center gap-2 disabled:opacity-50 font-semibold"
                >
                  <FiDownload />{' '}
                  {pdfLoadingId === viewInvoice.id ? 'Generating…' : 'Download PDF'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setViewInvoice(null);
                    handleEdit(viewInvoice);
                  }}
                  className="px-4 py-2.5 rounded-xl bg-[#1a365d] text-white hover:bg-[#153050] flex items-center gap-2 font-semibold"
                >
                  <FiEdit2 /> Edit
                </button>
                <button
                  type="button"
                  onClick={() => setViewInvoice(null)}
                  className="px-4 py-2.5 rounded-xl border border-slate-300 hover:bg-slate-50 font-semibold text-slate-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
